import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtCurrency } from "../../lib/api";
import MobileHeader from "../../components/MobileHeader";
import { Button } from "../../components/ui/button";
import { Slider } from "../../components/ui/slider";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { CheckCircle2, MapPin, Sprout, CloudRain, Sun, Cloud, Thermometer, Droplets, Satellite, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const CROPS = [
    { value: "maize", label: "Maize" },
    { value: "cassava", label: "Cassava" },
    { value: "rice", label: "Rice" },
    { value: "wheat", label: "Wheat" },
    { value: "tomato", label: "Tomato" },
    { value: "cocoa", label: "Cocoa" },
];

const PURPOSES = [
    { value: "input_financing", label: "Input financing (seeds, fertilizer)" },
    { value: "equipment", label: "Equipment / tractor" },
    { value: "offtake", label: "Offtake-linked financing" },
];

const TENURES = [90, 180, 365];
const PRODUCT_MIN = 20000;
const PRODUCT_MAX = 2000000;
const COLOR = "#00D084";

function NDVIBadge({ ndvi, peak }) {
    const pct = Math.max(0, Math.min(100, (ndvi / 1) * 100));
    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1">
                <Satellite size={12} /> NDVI · Vegetation Index
            </div>
            <div className="flex items-baseline gap-2 mt-2">
                <span className="font-display text-3xl font-bold text-emerald-600">{ndvi}</span>
                <span className="text-xs text-slate-500">/ peak {peak}</span>
            </div>
            <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                    className="h-full rounded-full bg-gradient-to-r from-amber-400 via-lime-400 to-emerald-500"
                    style={{ width: `${pct}%` }}
                />
            </div>
            <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                <span>0.0</span><span>0.5</span><span>1.0</span>
            </div>
        </div>
    );
}

function WeatherIcon({ icon }) {
    const map = { sun: Sun, cloud: Cloud, rain: CloudRain, partly_cloudy: Cloud };
    const Icon = map[icon] || Sun;
    const color = icon === "rain" ? "#0066FF" : icon === "sun" ? "#FF6B00" : "#64748B";
    return <Icon size={18} style={{ color }} />;
}

export default function AgroCreditApply() {
    const nav = useNavigate();
    const [farms, setFarms] = useState([]);
    const [farmId, setFarmId] = useState("");
    const [tab, setTab] = useState("existing"); // "existing" | "new"
    const [newFarm, setNewFarm] = useState({
        farm_name: "",
        region: "Kaduna",
        lat: "10.5222",
        lng: "7.4383",
        hectares: "5",
        crop: "maize",
    });
    const [insights, setInsights] = useState(null);
    const [amount, setAmount] = useState(500000);
    const [tenure, setTenure] = useState(180);
    const [purpose, setPurpose] = useState("input_financing");
    const [busy, setBusy] = useState(false);
    const [quote, setQuote] = useState(null);

    // Load farms
    useEffect(() => {
        api.get("/agro/farms").then((r) => {
            setFarms(r.data);
            if (r.data.length > 0) {
                setFarmId(r.data[0].id);
                setTab("existing");
            } else {
                setTab("new");
            }
        });
    }, []);

    const activeFarm = farms.find((f) => f.id === farmId);
    const lat = activeFarm ? activeFarm.lat : parseFloat(newFarm.lat || "0");
    const lng = activeFarm ? activeFarm.lng : parseFloat(newFarm.lng || "0");
    const crop = activeFarm ? activeFarm.crop : newFarm.crop;
    const hectares = activeFarm ? activeFarm.hectares : parseFloat(newFarm.hectares || "1");

    // Fetch insights when key inputs change
    useEffect(() => {
        if (!lat || !lng || !crop) return;
        api.get(`/agro/insights?lat=${lat}&lng=${lng}&crop=${crop}&hectares=${hectares}`)
            .then((r) => setInsights(r.data))
            .catch(() => setInsights(null));
    }, [lat, lng, crop, hectares]);

    // Quote
    useEffect(() => {
        api.post("/loans/quote", { product: "AgroCredit", amount, tenure_days: tenure })
            .then((r) => setQuote(r.data)).catch(() => setQuote(null));
    }, [amount, tenure]);

    const ensureFarm = async () => {
        if (activeFarm) return activeFarm;
        if (!newFarm.farm_name) {
            toast.error("Enter a farm name");
            return null;
        }
        const r = await api.post("/agro/farms", {
            farm_name: newFarm.farm_name,
            region: newFarm.region,
            lat: parseFloat(newFarm.lat),
            lng: parseFloat(newFarm.lng),
            hectares: parseFloat(newFarm.hectares),
            crop: newFarm.crop,
        });
        setFarms((s) => [...s, r.data]);
        setFarmId(r.data.id);
        return r.data;
    };

    const submit = async () => {
        setBusy(true);
        try {
            const f = await ensureFarm();
            if (!f) return;
            const r = await api.post("/agro/apply", {
                farm_id: f.id,
                amount,
                tenure_days: tenure,
                purpose,
            });
            toast.success("AgroCredit disbursed!");
            nav(`/business/loans/detail/${r.data.loan.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="agro-apply-page">
            <MobileHeader
                title="AgroCredit™"
                subtitle="Satellite-driven farm finance"
                backTo="/business/loans"
                variant="blue"
            >
                <div className="mt-4 bg-white/15 backdrop-blur rounded-2xl p-4 flex items-center gap-3">
                    <div className="size-12 rounded-xl bg-emerald-400/30 flex items-center justify-center">
                        <Sprout className="text-emerald-200" size={22} />
                    </div>
                    <div className="text-white flex-1">
                        <div className="text-xs uppercase tracking-wider text-white/70">Powered by</div>
                        <div className="font-display font-semibold">EOSDA · Tomorrow.io · Sentinel-2</div>
                    </div>
                </div>
            </MobileHeader>

            <div className="px-5 -mt-4 pb-4 space-y-4">
                {/* Farm picker */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow">
                    <Tabs value={tab} onValueChange={setTab}>
                        <TabsList className="grid grid-cols-2 w-full" data-testid="farm-tabs">
                            <TabsTrigger value="existing" disabled={farms.length === 0} data-testid="tab-existing">My farms</TabsTrigger>
                            <TabsTrigger value="new" data-testid="tab-new-farm">Add new</TabsTrigger>
                        </TabsList>
                    </Tabs>

                    {tab === "existing" && farms.length > 0 && (
                        <div className="mt-3">
                            <Select value={farmId} onValueChange={setFarmId}>
                                <SelectTrigger className="h-11 rounded-xl" data-testid="farm-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {farms.map((f) => (
                                        <SelectItem key={f.id} value={f.id}>
                                            {f.name} · {f.region} · {f.hectares}ha · {f.crop}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {tab === "new" && (
                        <div className="mt-3 space-y-3">
                            <div>
                                <Label className="text-xs">Farm name</Label>
                                <Input value={newFarm.farm_name} onChange={(e) => setNewFarm((s) => ({ ...s, farm_name: e.target.value }))} className="mt-1 h-10 rounded-xl" data-testid="farm-name" />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label className="text-xs">Region</Label>
                                    <Input value={newFarm.region} onChange={(e) => setNewFarm((s) => ({ ...s, region: e.target.value }))} className="mt-1 h-10 rounded-xl" data-testid="farm-region" />
                                </div>
                                <div>
                                    <Label className="text-xs">Hectares</Label>
                                    <Input type="number" value={newFarm.hectares} onChange={(e) => setNewFarm((s) => ({ ...s, hectares: e.target.value }))} className="mt-1 h-10 rounded-xl" data-testid="farm-hectares" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label className="text-xs">Latitude</Label>
                                    <Input type="number" step="0.0001" value={newFarm.lat} onChange={(e) => setNewFarm((s) => ({ ...s, lat: e.target.value }))} className="mt-1 h-10 rounded-xl" data-testid="farm-lat" />
                                </div>
                                <div>
                                    <Label className="text-xs">Longitude</Label>
                                    <Input type="number" step="0.0001" value={newFarm.lng} onChange={(e) => setNewFarm((s) => ({ ...s, lng: e.target.value }))} className="mt-1 h-10 rounded-xl" data-testid="farm-lng" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs">Crop</Label>
                                <Select value={newFarm.crop} onValueChange={(v) => setNewFarm((s) => ({ ...s, crop: v }))}>
                                    <SelectTrigger className="mt-1 h-10 rounded-xl" data-testid="farm-crop"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {CROPS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}
                </div>

                {/* Satellite map mock + NDVI */}
                {insights && (
                    <>
                        <div
                            className="rounded-2xl overflow-hidden relative h-44 border border-slate-200"
                            data-testid="satellite-map-mock"
                            style={{
                                background: `linear-gradient(135deg, #064e3b 0%, #15803d 30%, #65a30d 60%, #84cc16 100%)`,
                            }}
                        >
                            <div className="absolute inset-0 opacity-40 mix-blend-overlay"
                                 style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.6' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' /%3E%3C/svg%3E\")" }}
                            />
                            <div className="absolute inset-0 grid grid-cols-12 grid-rows-6">
                                {Array.from({ length: 72 }).map((_, i) => (
                                    <div key={i} className="border border-white/5" />
                                ))}
                            </div>
                            <div className="absolute bottom-3 left-3 bg-black/40 backdrop-blur rounded-lg px-3 py-2 text-white">
                                <div className="text-[10px] uppercase tracking-wider opacity-70 flex items-center gap-1"><MapPin size={10} /> {activeFarm?.region || newFarm.region}</div>
                                <div className="text-xs font-mono">{Number(lat).toFixed(4)}, {Number(lng).toFixed(4)}</div>
                            </div>
                            <div className="absolute top-3 right-3 bg-emerald-500/90 backdrop-blur rounded-lg px-3 py-2 text-white">
                                <div className="text-[10px] uppercase tracking-wider opacity-90">Health</div>
                                <div className="text-sm font-bold">{insights.satellite.vegetation_health}</div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <NDVIBadge ndvi={insights.satellite.ndvi} peak={insights.satellite.ndvi_peak} />
                            <div className="bg-white rounded-2xl border border-slate-200 p-4">
                                <div className="text-xs uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1">
                                    <TrendingUp size={12} /> Yield projection
                                </div>
                                <div className="font-display text-2xl font-bold mt-2 text-[#0A1930]">{insights.satellite.predicted_yield_t_ha}t<span className="text-sm font-medium text-slate-500">/ha</span></div>
                                <div className="text-xs text-slate-500 mt-1">≈ {fmtCurrency(insights.expected_revenue_ngn, "NGN", { compact: true })} revenue</div>
                                <div className="mt-2 inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-md"
                                     style={{
                                         background: insights.satellite.risk_grade === "Low" ? "#ecfdf5" : insights.satellite.risk_grade === "Medium" ? "#fffbeb" : "#fef2f2",
                                         color: insights.satellite.risk_grade === "Low" ? "#047857" : insights.satellite.risk_grade === "Medium" ? "#b45309" : "#b91c1c",
                                     }}
                                >
                                    {insights.satellite.risk_grade} risk
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-200 p-4">
                            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Field conditions</div>
                            <div className="grid grid-cols-3 gap-3">
                                <Stat icon={Thermometer} color="#FF6B00" label="Avg temp" value={`${insights.satellite.avg_temp_c}°C`} />
                                <Stat icon={CloudRain} color="#0066FF" label="Rain 30d" value={`${insights.satellite.rain_30d_mm}mm`} />
                                <Stat icon={Droplets} color="#06B6D4" label="Soil moist." value={`${(insights.satellite.soil_moisture * 100).toFixed(0)}%`} />
                            </div>
                            <div className="mt-4">
                                <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">7-day forecast</div>
                                <div className="grid grid-cols-7 gap-1">
                                    {insights.satellite.weather_outlook.map((d) => (
                                        <div key={d.day} className="bg-slate-50 rounded-lg p-2 text-center" data-testid={`forecast-${d.day}`}>
                                            <div className="text-[10px] text-slate-500">D{d.day}</div>
                                            <div className="my-1 flex justify-center"><WeatherIcon icon={d.icon} /></div>
                                            <div className="text-[10px] font-bold">{d.temp_c}°</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-4 flex items-center gap-3" data-testid="rec-limit-card">
                            <div className="size-11 rounded-xl bg-emerald-500 text-white flex items-center justify-center">
                                <Sprout size={20} />
                            </div>
                            <div className="flex-1">
                                <div className="text-xs uppercase tracking-wider text-emerald-700 font-bold">Recommended limit</div>
                                <div className="font-display text-xl font-bold text-emerald-700">{fmtCurrency(insights.recommended_limit_ngn, "NGN", { compact: true })}</div>
                            </div>
                            <div className="text-right">
                                <div className="text-xs text-emerald-700">Approval</div>
                                <div className="font-display font-bold text-emerald-700">{insights.approval_likelihood}%</div>
                            </div>
                        </div>
                    </>
                )}

                {/* Loan amount + tenure */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow">
                    <div className="text-sm font-semibold text-slate-700">How much do you need?</div>
                    <div className="text-center mt-4">
                        <div className="font-display text-3xl font-bold" style={{ color: COLOR }}>{fmtCurrency(amount)}</div>
                    </div>
                    <Slider min={PRODUCT_MIN} max={Math.min(PRODUCT_MAX, insights?.recommended_limit_ngn || PRODUCT_MAX)} step={5000} value={[amount]} onValueChange={(v) => setAmount(v[0])} className="mt-5" data-testid="agro-amount-slider" />
                    <div className="flex justify-between text-xs text-slate-400 mt-3">
                        <span>{fmtCurrency(PRODUCT_MIN, "NGN", { compact: true })}</span>
                        <span>{fmtCurrency(Math.min(PRODUCT_MAX, insights?.recommended_limit_ngn || PRODUCT_MAX), "NGN", { compact: true })}</span>
                    </div>
                    <div className="mt-5 grid grid-cols-3 gap-2">
                        {TENURES.map((t) => (
                            <button key={t} onClick={() => setTenure(t)} data-testid={`agro-tenure-${t}`} className={`py-3 rounded-xl border-2 text-sm font-semibold tap-feedback transition ${tenure === t ? "text-white" : "border-slate-200 text-slate-600"}`} style={tenure === t ? { background: COLOR, borderColor: COLOR } : {}}>
                                {t}d
                            </button>
                        ))}
                    </div>
                    <div className="mt-4">
                        <Label className="text-xs">Purpose</Label>
                        <Select value={purpose} onValueChange={setPurpose}>
                            <SelectTrigger className="h-10 mt-1 rounded-xl" data-testid="agro-purpose"><SelectValue /></SelectTrigger>
                            <SelectContent>{PURPOSES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>

                {quote && (
                    <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow">
                        <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Repayment</div>
                        <div className="font-display text-2xl font-bold mt-1">{fmtCurrency(quote.total_repayment)}</div>
                        <div className="text-xs text-slate-500">Due in {tenure} days</div>
                        <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                            <div><div className="text-slate-500">Principal</div><div className="font-bold">{fmtCurrency(quote.amount, "NGN", { compact: true })}</div></div>
                            <div><div className="text-slate-500">Interest</div><div className="font-bold">{fmtCurrency(quote.interest, "NGN", { compact: true })}</div></div>
                            <div><div className="text-slate-500">Fees</div><div className="font-bold">{fmtCurrency(quote.fees, "NGN", { compact: true })}</div></div>
                        </div>
                    </div>
                )}

                <Button onClick={submit} disabled={busy || !insights} className="w-full h-12 rounded-xl font-semibold" style={{ background: COLOR }} data-testid="agro-submit-btn">
                    {busy ? "Approving…" : "Apply with Satellite Data"}
                </Button>
                <div className="text-center text-xs text-slate-500 flex justify-center gap-1 items-center">
                    <CheckCircle2 size={12} className="text-emerald-500" /> Underwriting uses live NDVI + 30-day weather
                </div>
            </div>
        </div>
    );
}

function Stat({ icon: Icon, color, label, value }) {
    return (
        <div className="bg-slate-50 rounded-xl p-2.5">
            <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold text-slate-500 tracking-wider">
                <Icon size={12} style={{ color }} />
                {label}
            </div>
            <div className="font-display font-bold mt-1 text-sm">{value}</div>
        </div>
    );
}
